import { Alert, Input, Spin, Table } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { Grid, GridCell, GridRow, Condition, FilterGroup, Filter } from '../models';
import { apiClient } from '../utils';

export function RecordsPreview(props: { dataSetId?: string; filter?: Filter; filterGroup?: FilterGroup; conditions?: Condition }) {
  const [dataSet, setDataSet] = useState<Grid>();
  const [records, setRecords] = useState<GridRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [tableColumns, setTableColumns] = useState<any[]>();
  const [tableCurrent, setTableCurrent] = useState(1);
  const [tablePageSize, setTablePageSize] = useState(10);
  const [tableTotal, setTableTotal] = useState(0);

  const [search, setSearch] = useState<string>();

  const loadDataSet = useCallback(() => {
    if (!props.dataSetId) {
      return;
    }

    apiClient
      .get(`grids/${props.dataSetId}?includeAllFields=true`)
      .then((response: any) => {
        const dataSet = response.data as Grid;
        if (!dataSet) {
          setError('Veri seti bulunamadı.');
        }
        setDataSet(dataSet);
      })
      .catch((error: any) => {
        setError(error.message);
      });
  }, [props.dataSetId]);

  const loadRecords = useCallback(() => {
    if (!props.dataSetId) {
      return;
    }

    setLoading(true);
    setError(null);

    let offset = (tableCurrent - 1) * tablePageSize;

    const requestBody: any = {
      gridId: props.dataSetId,
      limit: tablePageSize,
      offset: offset,
    };

    if (props.filterGroup) {
      requestBody.filterGroup = props.filterGroup;
    }

    if (props.filter) {
      requestBody.filter = props.filter;
    }

    if (props.conditions) {
      requestBody.conditions = props.conditions;
    }

    apiClient
      .post(`records/records/query`, requestBody)
      .then((response: any) => {
        const responseData = response.data;

        if (!responseData?.records?.length) {
          setRecords([]);
          setTableTotal(0);
          setTableColumns(undefined);
          setLoaded(true);
          return;
        }

        setLoaded(true);

        setRecords(responseData.records as GridRow[]);
        setTableTotal(responseData.count);
      })
      .catch((error: any) => {
        setError(error.message);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [props.conditions, props.dataSetId, props.filter, props.filterGroup, tableCurrent, tablePageSize]);

  useEffect(() => {
    loadDataSet();
    loadRecords();
  }, [loadDataSet, loadRecords]);

  useEffect(() => {
    if (!dataSet?.fields?.length || !records?.length) {
      return;
    }

    const fields = dataSet.fields;

    const columns: any[] = fields.map(f => {
      return {
        title: f.displayName || f.name || f.code,
        dataIndex: ['cells', f.id],
        key: f.id,
        render: (cell: GridCell) => cell?.value?.toString(),
        width: (f.displayName || f.name || f.code).length * 10 + 40,
      };
    });
    setTableColumns(columns);
  }, [dataSet, records]);

  const onTableChange = (pagination: any) => {
    setTableCurrent(pagination.current);
    setTablePageSize(pagination.pageSize);
  };

  const onSearch = (input: string) => {
    input = input.trim();
    setSearch(input === '' ? undefined : input);
  };

  if (!props.dataSetId || (!props.filterGroup && !props.filter && !props.conditions) || (loaded && !dataSet?.fields?.length)) {
    return null;
  }

  return (
    <div style={{ padding: loading && !loaded ? '4rem 0' : 0, minHeight: '10rem' }}>
      <Spin spinning={loading && !loaded}>
        {error && <Alert message={error} type="error" showIcon />}
        {!error && loaded && records.length === 0 && <Alert message="Veri bulunamadı." type="info" showIcon />}
        {records.length > 0 && (
          <>
            <div style={{ textAlign: 'right', marginBottom: '1rem' }}>
              <Input.Search placeholder="Arama" allowClear={true} onSearch={onSearch} style={{ width: 200 }} />
            </div>
            <Table
              columns={tableColumns}
              dataSource={records}
              loading={loading}
              scroll={{ x: '100%', y: '50vh' }}
              bordered
              pagination={
                search
                  ? false
                  : {
                      current: tableCurrent,
                      total: tableTotal,
                      pageSize: tablePageSize,
                      pageSizeOptions: ['10', '25', '50', '100'],
                      position: ['bottomCenter'],
                      showTotal: (total, range) => `Toplam ${total} kayıt. ${range[0]}-${range[1]} arası gösteriliyor.`,
                      showSizeChanger: true,
                    }
              }
              onChange={onTableChange}
            />
          </>
        )}
      </Spin>
    </div>
  );
}
